import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api } from "../api";
import { AgentPerformanceChart } from "../components/analytics/agent-performance-chart";
import { CompletedByTypeChart } from "../components/analytics/completed-by-type-chart";
import { CreatedVsCompletedChart } from "../components/analytics/created-vs-completed-chart";
import { CycleTimeChart } from "../components/analytics/cycle-time-chart";
import { Kpi } from "../components/analytics/kpi";
import { formatHours, percent, totalsByWeek } from "../components/analytics/metrics";
import { SlaChart } from "../components/analytics/sla-chart";
import { TypeBreakdownTable } from "../components/analytics/type-breakdown-table";
import { TypeCostTable } from "../components/analytics/type-cost-table";
import { TypeIcon, useIssuesMeta } from "../components/issues/meta";
import { PageHeader, PageSkeleton } from "../components/page";
import { StatStrip } from "../components/stat-strip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const RANGES = [4, 8, 12, 26] as const;
const ALL = "__all__";

export function AnalyticsPage() {
  const [weeks, setWeeks] = useState<number>(12);
  const [type, setType] = useState<string>(ALL);
  const meta = useIssuesMeta().data;
  const query = useQuery({
    queryKey: ["analytics", weeks, type],
    queryFn: () => api.analyticsWeekly(weeks, type === ALL ? undefined : type),
    refetchInterval: 60_000,
  });
  const rows = useMemo(() => query.data?.rows ?? [], [query.data]);
  const totals = useMemo(() => totalsByWeek(rows), [rows]);
  const thisWeek = totals.at(-1);
  const lastWeek = totals.at(-2);
  const typesQuery = useQuery({
    queryKey: ["analytics-types", weeks, type],
    queryFn: () => api.analyticsTypes(weeks, type === ALL ? undefined : type),
    refetchInterval: 60_000,
  });
  const typeRows = useMemo(() => typesQuery.data?.rows ?? [], [typesQuery.data]);
  const agentsQuery = useQuery({
    queryKey: ["analytics-agents"],
    queryFn: api.analyticsAgents,
    refetchInterval: 60_000,
  });
  const agentRows = useMemo(() => agentsQuery.data?.rows ?? [], [agentsQuery.data]);

  if (query.isLoading && !query.data) return <PageSkeleton />;

  return (
    <>
      <PageHeader
        title="Analytics"
        description="Weekly performance of the issue pipeline, overall and by type."
        actions={
          <div className="flex items-center gap-2">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger size="sm" className="w-40">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {(meta?.types ?? []).map((t) => (
                  <SelectItem key={t.key} value={t.key}>
                    <TypeIcon typeKey={t.key} color={t.color} /> {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tabs value={String(weeks)} onValueChange={(v) => setWeeks(Number(v))}>
              <TabsList>
                {RANGES.map((range) => (
                  <TabsTrigger key={range} value={String(range)}>
                    {range}w
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        }
      />

      <StatStrip columns={6}>
        <Kpi label="Created this week" value={thisWeek?.created ?? 0} prev={lastWeek?.created} />
        <Kpi label="Completed" value={thisWeek?.completed ?? 0} prev={lastWeek?.completed} />
        <Kpi label="Open at week end" value={thisWeek?.openAtEnd ?? 0} prev={lastWeek?.openAtEnd} invert />
        <Kpi
          label="Median cycle time"
          value={formatHours(thisWeek?.medianCycleHours)}
          prevLabel={formatHours(lastWeek?.medianCycleHours)}
        />
        <Kpi
          label="SLA met"
          value={percent(thisWeek?.slaMet, thisWeek?.slaTotal)}
          prevLabel={percent(lastWeek?.slaMet, lastWeek?.slaTotal)}
        />
        <Kpi label="Story points shipped" value={thisWeek?.storyPoints ?? 0} prev={lastWeek?.storyPoints} />
      </StatStrip>

      <div className="grid gap-6 xl:grid-cols-2">
        <CreatedVsCompletedChart totals={totals} />
        <CompletedByTypeChart rows={rows} meta={meta} />
        <CycleTimeChart totals={totals} />
        <SlaChart totals={totals} />
      </div>

      <TypeBreakdownTable rows={rows} meta={meta} week={thisWeek?.week} />

      <TypeCostTable
        rows={typeRows}
        meta={meta}
        weeks={typesQuery.data?.weeks ?? weeks}
        periodStart={typesQuery.data?.periodStart}
      />

      <AgentPerformanceChart rows={agentRows} />
    </>
  );
}
